import React, { useState, useEffect } from 'react';
import { Modal, Table, Spinner, Button, Pagination, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import { getRfiByCreatorId } from '../../service/api/RfiService';

const MesRfiModal = ({ show, onClose, creatorId }) => {
    const selectedDomain = useSelectedDomain();
    const [rfis, setRfis] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [creatorName, setCreatorName] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return 'warning';
            case 'Completed': return 'success';
            case 'Canceled': return 'danger';
            default: return 'secondary';
        }
    };

    useEffect(() => {
        if (show && creatorId && selectedDomain && selectedDomain.id) {
            setIsLoading(true);
            const fetchRfis = async () => {
                try {
                    const data = await getRfiByCreatorId(creatorId, selectedDomain.id);
                    if (Array.isArray(data)) {
                        setRfis(data);
                        setCreatorName(data.length > 0 ? data[0].creatorName || 'Nom du créateur inconnu' : '');
                    } else {
                        toast.info(data);
                        setRfis([]);
                        setCreatorName('');
                    }
                } catch (error) {
                    toast.error('Erreur lors de la récupération des RFIs.');
                    setRfis([]);
                    setCreatorName('');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchRfis();
        }
    }, [show, creatorId, selectedDomain]);

    function formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = rfis.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Mes RFIs</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {creatorName && <div className="mb-3">Créateur : <strong className='text-primary'>{creatorName}</strong></div>}
                {isLoading ? (
                    <div className="d-flex justify-content-center">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </Spinner>
                    </div>
                ) : rfis.length > 0 ? (
                    <>
                        <Table striped hover responsive className="table table-striped table-hover table-responsive-md">
                            <thead>
                                <tr>
                                   
                                    <th>Titre</th>
                                    <th>Side</th>
                                    <th>Prix</th>
                                    <th>Quantité</th>
                                    <th>Quantité minimum</th>
                                    <th>Status</th>
                                    <th>Creation Date</th>
                                    <th>Last Update Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((rfi) => (
                                    <tr key={rfi.id}>
                                        <td><strong className='text-primary'>{rfi.securityName}</strong></td>
                                        <td>{rfi.side}</td>
                                        <td>{rfi.price}</td>
                                        <td>{rfi.quantity}</td>
                                        <td>{rfi.minQuantity}</td>
                                        <td>
                                            <span className={`badge rounded bg-${getStatusColor(rfi.status)}`}>{rfi.status}</span>
                                        </td>
                                        <td>{formatDate(rfi.creationDate)}</td>
                                        <td>{rfi.lastUpdateDate ? formatDate(rfi.lastUpdateDate) : 'N/A'}</td>
                                        <td>
                                            <OverlayTrigger overlay={<Tooltip>Editer</Tooltip>} placement="top">
                                                <Button variant="link" className="text-primary p-0 me-2">
                                                    <FaEdit size={18} />
                                                </Button>
                                            </OverlayTrigger>
                                            <OverlayTrigger overlay={<Tooltip>Supprimer</Tooltip>} placement="top">
                                                <Button variant="link" className="text-danger p-0">
                                                    <FaTrashAlt size={18} />
                                                </Button>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <Pagination className="justify-content-center">
                            <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                            {[...Array(Math.ceil(rfis.length / itemsPerPage))].map((_, index) => (
                                <Pagination.Item key={index + 1} active={index + 1 === currentPage} onClick={() => handlePageChange(index + 1)}>
                                    {index + 1}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next disabled={currentPage === Math.ceil(rfis.length / itemsPerPage)} onClick={() => handlePageChange(currentPage + 1)} />
                        </Pagination>
                    </>
                ) : (
                    <div className="text-center">Aucune RFI trouvée.</div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Fermer</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MesRfiModal;